import { useRef, useState, type FC } from "react";
import useDarkMode from "@/hooks/useDarkMode";
import ChartStateBody from "@/pages/CampaignOverviewPage/components/ChartStateBody";
import FilterSelect from "@/pages/CampaignOverviewPage/charts/DeviceBrowserChart/FilterSelect";
import { buildDeviceBrowserOption } from "@/pages/CampaignOverviewPage/charts/DeviceBrowserChart/deviceBrowserChartOptions";
import {
  computeAllOptions,
  useDeviceBrowserData,
  type MultiFilterState,
} from "@/pages/CampaignOverviewPage/charts/DeviceBrowserChart/useDeviceBrowserData";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";
import styles from "@/pages/CampaignOverviewPage/charts/DeviceBrowserChart/DeviceBrowserChart.module.css";

interface DeviceBrowserChartProps {
  error: string | null;
  isLoading: boolean;
  // The browser the "Top browser by CTR" tile names — always charted, even
  // when it falls outside the top browsers by impressions.
  leader?: string | null;
  rows: PerformanceReportRow[];
}

const CHART_STYLE = { height: "100%", width: "100%" } as const;

const T = TEXT.campaignOverview.deviceBrowser;

// One pill per dimension, in display order. Keying options, value and
// onChange off the same `dimension` keeps each pill wired to one filter.
const FILTER_PILLS: {
  dimension: keyof MultiFilterState;
  filterPlaceholder: string;
  placeholder: string;
  selectedItemsLabel: string;
}[] = [
  {
    dimension: "devices",
    filterPlaceholder: T.searchDevicesPlaceholder,
    placeholder: T.deviceType,
    selectedItemsLabel: "{0} devices",
  },
  {
    dimension: "osTypes",
    filterPlaceholder: T.searchOsTypesPlaceholder,
    placeholder: T.osType,
    selectedItemsLabel: "{0} OS types",
  },
  {
    dimension: "browsers",
    filterPlaceholder: T.searchBrowsersPlaceholder,
    placeholder: T.browser,
    selectedItemsLabel: "{0} browsers",
  },
];

const DeviceBrowserChart: FC<DeviceBrowserChartProps> = ({
  error,
  isLoading,
  leader = null,
  rows,
}) => {
  const isDark = useDarkMode();
  const [filters, setFilters] = useState<MultiFilterState>(() =>
    computeAllOptions(rows)
  );
  const prevRowsRef = useRef(rows);

  const { allRowOptions, bars, filterOptions } = useDeviceBrowserData(
    rows,
    filters,
    leader
  );

  const option = buildDeviceBrowserOption(bars, isDark);

  // Render-phase reset: fires synchronously so the stale filter state is never
  // committed to the DOM when the campaign or date selection changes.
  if (prevRowsRef.current !== rows) {
    prevRowsRef.current = rows;
    setFilters(allRowOptions);
  }

  return (
    <div className={styles.card}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitleGroup}>
          <span className={styles.panelTitle}>{T.panelTitle}</span>
          <span className={styles.panelSubtitle}>{T.panelSubtitle}</span>
        </div>

        <div
          aria-label={TEXT.campaignOverview.deviceBrowser.dataFilters}
          className={styles.filters}
          role="group"
        >
          {FILTER_PILLS.map(
            ({
              dimension,
              filterPlaceholder,
              placeholder,
              selectedItemsLabel,
            }) => (
              <FilterSelect
                key={dimension}
                filterPlaceholder={filterPlaceholder}
                options={filterOptions[dimension]}
                placeholder={placeholder}
                selectedItemsLabel={selectedItemsLabel}
                value={filters[dimension]}
                onChange={(values) =>
                  setFilters((prev) => ({ ...prev, [dimension]: values }))
                }
              />
            )
          )}
        </div>
      </div>

      <div className={styles.chartArea}>
        <ChartStateBody
          error={error}
          errorMessage={T.error}
          isEmpty={bars.length === 0}
          isLoading={isLoading}
          loadingClassName={styles.loading}
          messageClassName={styles.message}
          noDataMessage={T.noData}
          option={option}
          style={CHART_STYLE}
        />
      </div>
    </div>
  );
};

export default DeviceBrowserChart;
